import { createApp, h, reactive } from 'vue';
import router from './router';
import AppContainer from './AppContainer.vue';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import CodeHighlight from './AppCodeHighlight';

// PrimeVue 4 CSS imports - new architecture
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css'; 
import 'prismjs/themes/prism-coy.css';

// Import PrimeVue theme
import Aura from '@primevue/themes/aura';
// Import custom preset alternative
 import CustomAura from './themes/custom-aura-preset';

// Import global styles (without Serenity theme)
import './assets/component-overrides.css';
import './App.scss';

// Directive imports
import BadgeDirective from 'primevue/badgedirective';
import Ripple from 'primevue/ripple';
import Tooltip from 'primevue/tooltip';

// Component imports - only import what you actually use for better performance
import AutoComplete from 'primevue/autocomplete';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Breadcrumb from 'primevue/breadcrumb';
import DatePicker from 'primevue/datepicker';
import Card from 'primevue/card';
import Carousel from 'primevue/carousel';
import Chart from 'primevue/chart';
import Checkbox from 'primevue/checkbox';
import Chip from 'primevue/chip';
import Chips from 'primevue/chips';
import ColorPicker from 'primevue/colorpicker';
import Column from 'primevue/column';
import ConfirmDialog from 'primevue/confirmdialog';
import ConfirmPopup from 'primevue/confirmpopup';
import ContextMenu from 'primevue/contextmenu';
import DataTable from 'primevue/datatable';
import DataView from 'primevue/dataview';
import Dialog from 'primevue/dialog';
import Divider from 'primevue/divider';
import Select from 'primevue/select';
import Fieldset from 'primevue/fieldset';
import FileUpload from 'primevue/fileupload';
import InlineMessage from 'primevue/inlinemessage';
import Inplace from 'primevue/inplace';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import ToggleSwitch from 'primevue/toggleswitch';
import InputText from 'primevue/inputtext';
import Galleria from 'primevue/galleria';
import Knob from 'primevue/knob';
import Listbox from 'primevue/listbox';
import MegaMenu from 'primevue/megamenu';
import Menu from 'primevue/menu';
import Menubar from 'primevue/menubar';
import Message from 'primevue/message';
import MultiSelect from 'primevue/multiselect';
import OrderList from 'primevue/orderlist';
import OrganizationChart from 'primevue/organizationchart';
import Popover from 'primevue/popover';
import Paginator from 'primevue/paginator';
import Panel from 'primevue/panel';
import PanelMenu from 'primevue/panelmenu';
import Password from 'primevue/password';
import PickList from 'primevue/picklist';
import ProgressBar from 'primevue/progressbar';
import Rating from 'primevue/rating';
import RadioButton from 'primevue/radiobutton';
import SelectButton from 'primevue/selectbutton';
import ScrollPanel from 'primevue/scrollpanel';
import ScrollTop from 'primevue/scrolltop';
import Skeleton from 'primevue/skeleton';
import Slider from 'primevue/slider';
import Drawer from 'primevue/drawer';
import SplitButton from 'primevue/splitbutton';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Steps from 'primevue/steps';
import TabMenu from 'primevue/tabmenu';
import Tag from 'primevue/tag';
import TieredMenu from 'primevue/tieredmenu';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import Toolbar from 'primevue/toolbar';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Timeline from 'primevue/timeline';
import ToggleButton from 'primevue/togglebutton';
import Tree from 'primevue/tree';
import TreeTable from 'primevue/treetable';

import dayjs from 'dayjs'
var customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)

//import useVuelidate from '@vuelidate/core'
//import Vuelidate from 'vuelidate';
import { store } from './store/';

import { UserService } from '@/service/backend/user.service';

import ApiService from '@/service/backend/api.service';
ApiService.init();

/*router.beforeEach(function(to, from, next) {
    window.scrollTo(0, 0);
    next();
});*/

const app = createApp({
    render () { return h(AppContainer) }
});

app.config.globalProperties.$appState = reactive({ inputStyle: 'outlined' });
app.config.globalProperties.$dayjs = dayjs;

app.use(PrimeVue, {
    theme: {
        preset: CustomAura,
        options: {
            prefix: 'p',
            darkModeSelector: '.app-dark',
            cssLayer: false
        }
    },
    pt: {
        global: {
            css: `
                :root {
                    --p-primary-50: #ECEFF1;
                    --p-primary-100: #CFD8DC;
                    --p-primary-200: #B0BEC5;
                    --p-primary-300: #90A4AE;
                    --p-primary-400: #78909C;
                    --p-primary-500: #607D8B;
                    --p-primary-600: #546E7A;
                    --p-primary-700: #455A64;
                    --p-primary-800: #37474F;
                    --p-primary-900: #263238;
                    --p-primary-color: #607D8B;
                    --p-primary-contrast-color: #ffffff;
                }
            `
        }
    },
    ripple: true,
    locale: {
        startsWith: 'Kezdődik',
        contains: 'Tartalmaz',
        notContains: 'Nem tartalmaz',
        endsWith: 'Végződik',
        equals: 'Egyenlő',
        notEquals: 'Nem egyenlő',
        noFilter: 'Szűrő törlése',
        lt: 'Kisebb',
        lte: 'Kisebb vagy egyenlő',
        gt: 'Nagyobb',
        gte: 'Nagyobb vagy egyenlő',
        dateIs: 'Adott napon',
        dateIsNot: 'Ezen a napon nem',
        dateBefore: 'Előtt',
        dateAfter: 'Után',
        clear: 'Töröl',
        apply: 'Alkalmaz',
        matchAll: 'Mindre illik',
        matchAny: 'Bármelyikre illik',
        addRule: 'Szabály hozzáadása',
        removeRule: 'Szabály eltávolítása',
        accept: 'Igen',
        reject: 'Nem',
        dateFormat: 'yy.mm.dd',
        dayNames: ["Vasárnap", "Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat"],
        dayNamesShort: ["Vas", "Hét", "Kedd", "Sze", "Csü", "Pén", "Szo"],
        dayNamesMin: ["V","H","K","S","C","P","S"],
        monthNames: ["Január","Február","Március","Április","Május","Június","Július","Augusztus","Szeptember","Október","November","December"],
        monthNamesShort: ["Jan", "Feb", "Mar", "Ápr", "Máj", "Jún","Júl", "Aug", "Sze", "Okt", "Nov", "Dec"],
        today: 'Ma',
        weekHeader: 'Hét',
        firstDayOfWeek: 1,
    }
});

app.use(ConfirmationService);
app.use(ToastService);
//app.use(Vuelidate);

app.use(router);

app.use(store);

// app.use(UserService);
// main.js:201 [Vue warn]: A plugin must either be a function or an object with an "install" function.
//  call its methods like UserService.login(), UserService.logout(), etc.

app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);
app.directive('code', CodeHighlight);
app.directive('badge', BadgeDirective);

app.component('Accordion', Accordion);
app.component('AccordionTab', AccordionTab);
app.component('AutoComplete', AutoComplete);
app.component('Avatar', Avatar);
app.component('AvatarGroup', AvatarGroup);
app.component('Badge', Badge);
app.component('Breadcrumb', Breadcrumb);
app.component('Button', Button);
app.component('DatePicker', DatePicker);
app.component('Card', Card);
app.component('Carousel', Carousel);
app.component('Chart', Chart);
app.component('Checkbox', Checkbox);
app.component('Chip', Chip);
app.component('Chips', Chips);
app.component('ColorPicker', ColorPicker);
app.component('Column', Column);
app.component('ConfirmDialog', ConfirmDialog);
app.component('ConfirmPopup', ConfirmPopup);
app.component('ContextMenu', ContextMenu);
app.component('DataTable', DataTable);
app.component('DataView', DataView);
app.component('Dialog', Dialog);
app.component('Divider', Divider);
app.component('Select', Select);
app.component('Fieldset', Fieldset);
app.component('FileUpload', FileUpload);
app.component('InlineMessage', InlineMessage);
app.component('Inplace', Inplace);
app.component('InputMask', InputMask);
app.component('InputNumber', InputNumber);
app.component('ToggleSwitch', ToggleSwitch);
app.component('InputText', InputText);
app.component('Galleria', Galleria);
app.component('Knob', Knob);
app.component('Listbox', Listbox);
app.component('MegaMenu', MegaMenu);
app.component('Menu', Menu);
app.component('Menubar', Menubar);
app.component('Message', Message);
app.component('MultiSelect', MultiSelect);
app.component('OrderList', OrderList);
app.component('OrganizationChart', OrganizationChart);
app.component('Popover', Popover);
app.component('Paginator', Paginator);
app.component('Panel', Panel);
app.component('PanelMenu', PanelMenu);
app.component('Password', Password);
app.component('PickList', PickList);
app.component('ProgressBar', ProgressBar);
app.component('RadioButton', RadioButton);
app.component('Rating', Rating);
app.component('SelectButton', SelectButton);
app.component('ScrollPanel', ScrollPanel);
app.component('ScrollTop', ScrollTop);
app.component('Skeleton', Skeleton);
app.component('Slider', Slider);
app.component('Drawer', Drawer);
app.component('SplitButton', SplitButton);
app.component('Splitter', Splitter);
app.component('SplitterPanel', SplitterPanel);
app.component('Steps', Steps);
app.component('TabMenu', TabMenu);
app.component('TabView', TabView);
app.component('TabPanel', TabPanel);
app.component('Tag', Tag);
app.component('Textarea', Textarea);
app.component('TieredMenu', TieredMenu);
app.component('Timeline', Timeline);
app.component('Toast', Toast);
app.component('Toolbar', Toolbar);
app.component('ToggleButton', ToggleButton);
app.component('Tree', Tree);
app.component('TreeTable', TreeTable);

app.mount('#app');